import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook for Enter-to-next-field and last-field-saves behavior.
 * Call getFieldProps(index) for each field in order.
 * Dropdowns: attach onKeyDown from getFieldProps to the SelectTrigger.
 */
export function useFormKeyboard(totalFields, onSave) {
  const focusField = useCallback((index) => {
    // Try multiple selector strategies
    const el =
      document.querySelector(`[data-form-idx="${index}"]`) ||
      document.querySelector(`[data-form-idx="${index}"] button`) ||
      document.querySelector(`[data-form-idx="${index}"] input`);
    if (el) {
      el.focus();
      // For Radix Select triggers, we need to click to open
      if (el.getAttribute('role') === 'combobox') {
        // Just focus, don't open
        el.focus();
      }
    }
  }, []);

  const handleKeyDown = useCallback((e, fieldIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (fieldIndex >= totalFields - 1) {
        onSave();
      } else {
        focusField(fieldIndex + 1);
      }
    }
  }, [totalFields, onSave, focusField]);

  // Returns props to spread on Input or attach to Select trigger's onKeyDown
  const getFieldProps = useCallback((index) => ({
    'data-form-idx': index,
    tabIndex: 0,
    onKeyDown: (e) => handleKeyDown(e, index),
  }), [handleKeyDown]);

  return { getFieldProps, focusField };
}

/**
 * Hook for global Escape key: navigates to homepage from any module page
 * when no dialog/sheet/modal/overlay is open.
 */
export function useEscapeToHome() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;

      // Don't navigate if any overlay is open (dialog, sheet, popover, task detail, subtask detail)
      const hasOverlay = document.querySelector(
        '[role="dialog"], [role="alertdialog"], [data-state="open"][data-radix-popper-content-wrapper], [data-testid="task-detail"], [data-testid="subtask-detail"]'
      );
      if (hasOverlay) return;

      // Don't navigate if an input/select is focused
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        active.blur();
        return;
      }

      if (location.pathname !== '/') {
        navigate('/');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location.pathname]);
}
